import { ActionIcon, Group, NumberInput, Select } from "@mantine/core";
import { IconMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import type { DateTime } from "luxon";
import { Fragment, useCallback, useEffect } from "react";
import { DateInput } from "../../../../../mantine/dates/DateInput/DateInput";
import { THEME_VARS } from "../../../../../utils/constants";
import { LUXON_END_OF_LAST_YEAR } from "../../../../../utils/dates";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import type { EdaFilterOptions } from "../EnergyDataAcquisitionSelectionModal";
import {
  FILTER_TYPES,
  FILTER_TYPE_KEYS,
  INSTALLED_CAPACITY_DEFAULT_MIN_MAX
} from "./filter-utils";

interface FilterProps {
  type: string;
  availableTypes: Array<string>;
  options: Array<
    | string
    | {
        min: number;
        max: number;
      }
  >;
  possibleOptions: EdaFilterOptions;
  canRemove: boolean;
  onChangeType: (type: string) => void;
  onChangeOptions: (
    options: Array<
      | string
      | {
          min: number;
          max: number;
        }
    >
  ) => void;
  onRemoveFilter: () => void;
}

function Filter({
  type,
  availableTypes,
  options,
  possibleOptions,
  canRemove,
  onChangeType,
  onChangeOptions,
  onRemoveFilter
}: FilterProps) {
  function handleOptionChange(
    index: number,
    value: string | { min: number; max: number }
  ) {
    const newOptions = [...options];
    newOptions[index] = value;
    onChangeOptions(newOptions);
  }

  function handleAddOption() {
    onChangeOptions([...options, ""]);
  }

  function handleRemoveOption(index: number) {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    onChangeOptions(newOptions);
  }

  return (
    <Group align="center" className="filter" p={0}>
      <FilterTypeDropdown
        availableTypes={availableTypes}
        type={type}
        onChange={onChangeType}
      />
      {type !== "" && (
        <>
          {type === "installedCapacity" ? "zwischen" : "ist"}
          <FilterOptions
            filterType={type}
            options={options}
            possibleOptions={possibleOptions}
            onAddOption={handleAddOption}
            onOptionChange={handleOptionChange}
            onRemoveOption={handleRemoveOption}
          />
        </>
      )}
      {canRemove && <RemoveFilterButton onClick={onRemoveFilter} />}
    </Group>
  );
}

interface FilterTypeDropdownProps {
  type: string;
  availableTypes: Array<string>;
  onChange: (value: string) => void;
}

function FilterTypeDropdown({
  type,
  availableTypes,
  onChange
}: FilterTypeDropdownProps) {
  const availableOrCurrentFilters = FILTER_TYPE_KEYS.filter((filterType) => {
    return availableTypes.includes(filterType) || filterType === type;
  });

  return (
    <Select
      data={availableOrCurrentFilters.map((filterType) => ({
        value: filterType,
        label: FILTER_TYPES[filterType]
      }))}
      placeholder="Filtertyp auswählen"
      value={type}
      onChange={(newFilterType) => {
        if (newFilterType !== null) onChange(newFilterType);
      }}
    />
  );
}

interface FilterOptionsProps {
  options: Array<
    | string
    | {
        min: number;
        max: number;
      }
  >;
  filterType: string;
  possibleOptions: EdaFilterOptions;
  onOptionChange: (
    index: number,
    value:
      | string
      | {
          min: number;
          max: number;
        }
  ) => void;
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
}

function FilterOptions({
  options,
  filterType,
  possibleOptions,
  onOptionChange,
  onAddOption,
  onRemoveOption
}: FilterOptionsProps) {
  const availableOptions = possibleOptions.filter(
    (possibleOption) => !options.includes(possibleOption.value.toString())
  );

  return (
    <Group gap="sm">
      {options.map((option, index) => {
        if (filterType === "installedCapacity") {
          return (
            <FilterInstalledCapacityOption
              key={index}
              option={
                option === ""
                  ? INSTALLED_CAPACITY_DEFAULT_MIN_MAX
                  : (option as { min: number; max: number })
              }
              onChange={(value) => onOptionChange(index, value)}
            />
          );
        } else if (filterType === "date") {
          return (
            <FilterDateOption
              key={index}
              option={option === "" ? null : (option as string)}
              onChange={(value) => onOptionChange(index, value)}
            />
          );
        } else {
          return (
            <Fragment key={(option as string) || index}>
              <FilterDropdownOption
                availableOptions={availableOptions}
                option={option as string}
                possibleOptions={possibleOptions}
                onChange={(value) => onOptionChange(index, value.toString())}
              />
              {index < options.length - 1 && (
                <span className="connector-text">oder</span>
              )}
            </Fragment>
          );
        }
      })}
      {options.length < possibleOptions.length && (
        <ActionIcon
          color="secondary"
          size="sm"
          title="Option hinzufügen"
          variant="white"
          onClick={onAddOption}
        >
          <IconPlus />
        </ActionIcon>
      )}
      {options.length > 1 && (
        <ActionIcon
          color="secondary"
          size="sm"
          title="Option löschen"
          variant="white"
          onClick={() => onRemoveOption(options.length - 1)}
        >
          <IconMinus />
        </ActionIcon>
      )}
    </Group>
  );
}

function RemoveFilterButton({ onClick }) {
  return (
    <ActionIcon
      size="sm"
      title="Kriterium löschen"
      variant="white"
      onClick={onClick}
    >
      <IconTrash color={THEME_VARS.error[6]} />
    </ActionIcon>
  );
}

interface FilterDropdownOptionProps {
  option: string;
  possibleOptions: EdaFilterOptions;
  availableOptions: EdaFilterOptions;
  onChange: (value: string | number) => void;
}

function FilterDropdownOption({
  option,
  possibleOptions,
  availableOptions,
  onChange
}: FilterDropdownOptionProps) {
  function sanitizeChange(value: string | null) {
    if (value === "" || value === null) {
      return "";
    } else if (isNaN(value as unknown as number)) {
      // this is typescript headache - isNaN wants a number to be able to check if the value we don't know is a number is a number...
      return value;
    } else {
      return parseInt(value, 10);
    }
  }

  const selectedOption = possibleOptions.find(
    (possibleOption) => possibleOption.value.toString() === option
  );
  const availableOptionValues = availableOptions.map((ao) => ao.value);
  const availablePossibleOptions = possibleOptions.filter(
    (po) =>
      availableOptionValues.includes(po.value) || po.value.toString() === option
  );

  const optionsGroupedBySite = availablePossibleOptions.reduce(
    (accumulator, availablePossibleOption) => {
      const site = availablePossibleOption.site;
      const existingOptionsForSite =
        site in accumulator ? accumulator[site] : [];
      return {
        ...accumulator,
        [site]: [...existingOptionsForSite, availablePossibleOption]
      };
    },
    {}
  );
  const siteNames = Object.keys(optionsGroupedBySite);
  const siteNamesSorted = siteNames.sort();
  const optionsGroupedBySiteAndSorted = siteNames.reduce(
    (accumulator, site) => {
      return {
        ...accumulator,
        [site]: optionsGroupedBySite[site].sort()
      };
    },
    {}
  );

  const selectData =
    siteNames.length !== 0
      ? siteNamesSorted.length > 1
        ? siteNames.map((site) => ({
            group: site,
            items: optionsGroupedBySiteAndSorted[site].map((option) => ({
              value: option.value.toString(),
              label: option.label
            }))
          }))
        : optionsGroupedBySiteAndSorted[siteNames[0]].map((option) => ({
            value: option.value.toString(),
            label: option.label
          }))
      : [];

  return (
    <Select
      data={selectData}
      value={selectedOption?.value.toString()}
      onChange={(value) => onChange(sanitizeChange(value))}
    />
  );
}

interface FilterInstalledCapacityOptionProps {
  option: { min: number; max: number };
  onChange: (value: { min: number; max: number }) => void;
}

function FilterInstalledCapacityOption({
  option,
  onChange
}: FilterInstalledCapacityOptionProps) {
  function handleChangeMin(value: number | string) {
    onChange({
      min: Number(value),
      max: option.max
    });
  }

  function handleChangeMax(value: number | string) {
    onChange({
      min: option.min,
      max: Number(value)
    });
  }

  return (
    <>
      <NumberInput
        min={0}
        step={1}
        value={option.min}
        onChange={handleChangeMin}
      />
      und
      <NumberInput step={1} value={option.max} onChange={handleChangeMax} />
    </>
  );
}

interface FilterDataOptionProps {
  option: string | null;
  onChange: (date: string) => void;
}

function FilterDateOption({ option, onChange }: FilterDataOptionProps) {
  const handleChangeFilterDate = useCallback(
    (date: DateTime | null) => {
      if (date === null) {
        return;
      }

      const backendDate = luxonDateTimeToBackendDateOrDateTime(date);
      onChange(backendDate);
    },
    [onChange]
  );

  useEffect(() => {
    if (!option) {
      handleChangeFilterDate(LUXON_END_OF_LAST_YEAR);
    }
  }, [option, handleChangeFilterDate]);

  const date = option
    ? backendDateOrDateTimeToLuxonDateTime(option)
    : LUXON_END_OF_LAST_YEAR;

  return (
    <DateInput
      id="eda-filters-date-filter"
      value={date}
      onChange={handleChangeFilterDate}
    />
  );
}

export { Filter };
